import React from 'react';
import styles from './SocialChat.scss';
import {
  AppMode,
  AVAILABILITY_STATUS,
  Avatar,
  AVATAR_TYPE,
  AvatarConfig,
  ChatRoomScreen,
  ChatWidget,
  expandCollapseManager,
  withGlobalUnread,
  FileSelectionMethods,
  MessageDisplayConfig,
  TitleConfig,
  ISendersData,
} from '@wix/chat-web';
import wix from '../../../services/wix';
import { EditorViewMode } from '../../../constants/editor-view-mode';
import * as biLogger from '../../../services/biLogger';
import { biSocialNewConversionEntryPoint } from '../../../services/biEvents';
import withMobile from '../../../hoc/mobile';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  isMessageNotificationEnabled,
  primaryColorOrDefault,
  theme,
} from '../../../reducers';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { mediaManager } from '@wix/inbox-common';
import {
  collapseWidget,
  expandWidget,
  goOffline,
  initApp,
  setCurrentChatroom,
  startPrivateChat,
} from '../../../actions';
import SocialChatList from '../../SocialChatList/SocialChatList';
import ChatCreation, { SiteMember } from '../../ChatCreation/ChatCreation';
import { InjectedTranslateProps, translate } from 'react-i18next';
import serverApi from '../../../services/serverApi';
import firebaseChatService from '../../../services/firebaseChatService';
import { fedopsLogger } from '../../../services/fedopsLogger';
import { MessagePopup } from '../../MessagePopup/MessagePopup';
import { chatSdk, DeliveryMethodId } from '@wix/chat-sdk';
import { AVATARS } from '../../../constants/avatars';
import { ViewerOnMessageSentEvent } from '../../../constants/viewer-event-types';
import { CTA_PRODUCT } from '../../../constants/call-to-action';
import ModalWidget from '../../ModalWidget/ModalWidget';
import { ChatroomMemberList } from '../../ChatroomMemberList/ChatroomMemberList';
import { ChatroomAddMembers } from '../../ChatroomAddMembers/ChatroomAddMembers';
import socialServerApi from '../../../services/socialServerApi';
import { ChatroomUpdate } from '../../ChatroomUpdate/ChatroomUpdate';
import HeaderMenu from '../../HeaderMenu/HeaderMenu';
import * as Sentry from '@sentry/browser';
import { withExperiments } from '@wix/wix-experiments-react';
import { WidgetSenderIdentityType } from '../../../constants/widget-sender-identity';
import { Experiments } from '../../../constants/experiments';

export const NO_HEADER = -1;

const ChatWidgetWithUnreadCount = withGlobalUnread(ChatWidget);

export interface ChatroomDisplayData {
  id: string;
  name: string;
  avatar: Avatar;
  userId?: string;
  createBy: string;
  isPrivate: boolean;
}

interface SocialChatProps extends InjectedTranslateProps {
  initApp: Function;
  firebaseGoOffline: Function;
  visibilitySettings: any;
  theme: any;
  viewMode: EditorViewMode;
  locale: string;
  experiments: any;
  editorSettingsLayout: any;
  editorSettingsBehaviour: any;
  localizedTexts: any;
  editorSettingsAvatar: Avatar;
  shardId: string;
  businessChatRoomId: string;
  isChatSdkInit: boolean;
  deviceType: string;
  onMessageReceived: Function;
  businessChatEnabled: boolean;
  widgetExpanded: boolean;
  currentChatroom: null | ChatroomDisplayData;
  isMessageNotificationEnabled: boolean;
  expandWidget(): any;
  collapseWidget(): any;
  availabilityStatus: any;
  setCurrentChatroom(room: null | ChatroomDisplayData): any;
  startPrivateChat({
    userId,
    requestId,
  }: {
    userId: string;
    requestId: string;
  }): any;
  visitorId: string;
  chatOnPage: boolean;
  primaryColor: string;
  host: string;
  widgetSenderIdentity: string;
  widgetSenderData: ISendersData;
  isResponsive: boolean;
}

const enum WidgetStates {
  leaveGroupChat,
  memberList,
  addMembersToRoom,
  removeMemberFromRoom,
  createPrivateChat,
  createGroupChat,
  updateGroupChat,
  chatroomList,
  chatroom,
}

interface SocialChatState {
  numberOfSelected: number;
  isGroupChat: boolean;
  participantCount: number;
  widgetStates: WidgetStates;
  participantIdToRemove: string;
  chatroom: any;
  isStateNotificationEnabled: boolean;
}

class SocialChat extends React.Component<SocialChatProps, SocialChatState> {
  constructor(props, context) {
    super(props, context);
    this.state = {
      numberOfSelected: 0,
      isGroupChat: false,
      participantCount: 0,
      widgetStates: this.props.currentChatroom
        ? WidgetStates.chatroom
        : WidgetStates.chatroomList,
      participantIdToRemove: '',
      chatroom: undefined,
      isStateNotificationEnabled: this.props.isMessageNotificationEnabled,
    };
  }

  NONE_AVATAR = { avatarType: AVATAR_TYPE.NONE };

  menuItems = [
    {
      title: this.props.t('social.group-chat.menu.members-update'),
      action: () => this.setState({ widgetStates: WidgetStates.memberList }),
      includeInGroupOwner: true,
    },
    {
      title: this.props.t('social.group-chat.menu.group-update'),
      action: () =>
        this.setState({ widgetStates: WidgetStates.updateGroupChat }),
      includeInGroupOwner: true,
    },
    {
      title: this.props.t('social.group-chat.menu.leave-group'),
      action: () =>
        this.setState({ widgetStates: WidgetStates.leaveGroupChat }),
      includeInGroupOwner: false,
    },
  ];

  async componentDidMount() {
    Sentry.addBreadcrumb({ category: 'MountedWidget', message: 'SocialChat' });
    expandCollapseManager.registerOnExpand(this.handleExpand);
    expandCollapseManager.registerOnCollapse(this.handleCollapse);
    wix.subscribeForStartPrivateChat(this.handleStartPrivateChat);
    this.showOrHideChat();
    await this.props.initApp({ isBusinessChat: false });

    wix
      .isResponsive()
      .then((isResponsive) => {
        if (isResponsive) {
          this.setState({ isStateNotificationEnabled: true });
        }
      })
      .catch(() => {});
  }

  handleStartPrivateChat = ({ data: { userId, requestId } }) => {
    this.props.startPrivateChat({ userId, requestId });
  };

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpand);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
    this.props.firebaseGoOffline();
  }

  shouldShowChat() {
    const {
      chatOnPage,
      visibilitySettings: { publicVisibility, chatOnboarded, editorVisibility },
    } = this.props;

    if (
      this.props.currentChatroom &&
      this.state.widgetStates === WidgetStates.chatroomList
    ) {
      this.setState({ widgetStates: WidgetStates.chatroom });
    }

    return this.isInSiteView()
      ? chatOnPage && publicVisibility && chatOnboarded
      : editorVisibility;
  }

  showOrHideChat() {
    if (!this.shouldShowChat()) {
      void this.hide();
    }
  }

  isInSiteView = () => this.props.viewMode === EditorViewMode.SITE;

  isMobile = () => this.props.deviceType === 'mobile';
  isResponsive = () => this.props.isResponsive;
  thereIsQAB = () => this.isMobile() && !this.isResponsive();
  isExpanded = () => this.thereIsQAB() || this.props.widgetExpanded;

  handleExpand = async ({ direction = 'uou', referrer = 'widget' } = {}) => {
    const { host } = this.props;
    const chatRoomType = this.state.isGroupChat ? 'group' : '';
    biLogger.expandChatEvent({ direction, referrer, chatRoomType, host });
    await this.props.expandWidget();
    //here should clear pimple if its there
  };

  handleCollapse = async () => {
    if (!this.thereIsQAB()) {
      return this.props.collapseWidget();
    }
  };

  hide() {
    wix.resizeToHidden(); // tslint:disable-line:no-floating-promises
  }

  onChatDisplayed = () => {
    if (this.shouldShowChat()) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );
      const layoutName = asRounded
        ? 'collapsedAsRounded'
        : 'selectedRadiusOption';
      biLogger.chatDisplayedEvent(this.props.deviceType, layoutName);
      fedopsLogger.appLoaded();
    }
  };

  eventHooks = {
    onSendMessage: (message) => this.onSendMessage(message),
    onAttachmentClick: () => this.onAttachmentClick(),
    onChatDisplayed: () => this.onChatDisplayed(),
    onCallToActionClick: (url) => this.onCallToActionClick(url),
  };

  onCallToActionClick = (url) => {
    biLogger.callToActionClickEvent(url);
    this.openProductPage(url);
  };

  triggerCollapse = () => expandCollapseManager.collapse();

  openProductPage = (url) => {
    this.isMobile() && this.triggerCollapse();
    const productRegex = /product-page\/([^?/\s]+)/gm;
    const productMatch = productRegex.exec(url);
    const fallbackBehavior = () => window.open(url);
    if (productMatch) {
      const productName = productMatch[1];
      wix.navigateToSection(
        {
          appDefinitionId: CTA_PRODUCT.APP_DEFINITION_ID,
          sectionId: CTA_PRODUCT.SECTION_ID,
        },
        productName,
        fallbackBehavior,
      );
    } else {
      fallbackBehavior();
    }
  };
  onSendMessage = (message) => {
    wix.publishToPubSub(ViewerOnMessageSentEvent, message);
    const chatRoomType = this.state.isGroupChat ? 'group' : '';
    const { host } = this.props;
    return biLogger.sendMessageEvent({
      chatRoomType,
      host,
    });
  };
  onAttachmentClick = () => biLogger.attachmentClickEvent();

  handleClickBack = () => {
    this.setState({
      isGroupChat: false,
      participantCount: 0,
      widgetStates: WidgetStates.chatroomList,
    });
    void this.handleChatroomSelection(null);
  };

  isNewGenMediaEnabled = () => {
    return (
      this.props.experiments.enabled('specs.media.MediaManager3') &&
      this.props.experiments.enabled('specs.crm.WixChatUsingMM3Spec')
    );
  };

  handleUploadFile = async (file: File, source: FileSelectionMethods) => {
    const { token, url, folderId } = await serverApi.getUploadToken();
    const isNewGen = this.isNewGenMediaEnabled();
    const effectiveFolderId = isNewGen ? folderId : undefined;

    if (source !== FileSelectionMethods.Input) {
      const { isGroupChat, chatroom } = this.state;
      const { currentChatroom, visitorId } = this.props;
      biLogger.fileUploadFromClipboard(
        file.name,
        file.size,
        file.type,
        isGroupChat ? 'group' : '',
        chatroom ? chatroom.id : currentChatroom && currentChatroom.id,
        visitorId,
        source === FileSelectionMethods.DragNDrop,
      );
    }

    return mediaManager.uploadFileToMedia({
      file,
      token,
      uploadUrlApi: url,
      folderId: effectiveFolderId,
    });
  };

  handleClickCreateNewChat = () => {
    biLogger.socialNewConversion(biSocialNewConversionEntryPoint.WIDGET);
    this.setState({ widgetStates: WidgetStates.createPrivateChat });
  };

  handleOnCreateNewChat = (chatroom) => {
    chatroom.createBy = this.props.visitorId;
    this.props.setCurrentChatroom(chatroom);
    this.setState({
      widgetStates: WidgetStates.chatroom,
      isGroupChat: !!chatroom.participantCount,
      participantCount: chatroom.participantCount,
    });
  };

  handleCreationCancel = () => {
    this.props.setCurrentChatroom(null);
    this.setState({
      widgetStates: WidgetStates.chatroomList,
    });
  };

  isBusinessChatroom = () => {
    const { businessChatRoomId, currentChatroom } = this.props;
    return currentChatroom && currentChatroom.id === businessChatRoomId;
  };

  handleOnClick = () => {
    if (this.isInSiteView()) {
      firebaseChatService.goOnlineIfOffline();
    }
  };

  handleChatroomSelection = async (chatroomId: string | null) => {
    const {
      businessChatRoomId,
      localizedTexts: { onlineTitle, offlineTitle },
      host,
    } = this.props;

    if (!chatroomId) {
      this.props.setCurrentChatroom(null);
      return;
    }

    const chatroom = await chatSdk.getChatroom({
      chatroomId,
    });

    const createBy = `${chatroom.createdBy}`;

    const { participantCount, id, isPrivate } = chatroom;

    const direction = 'uou';
    const referrer = 'widget';
    const chatRoomType = isPrivate ? '' : 'group';
    biLogger.enterChatroomEvent({ direction, referrer, chatRoomType, host });

    this.setState({ isGroupChat: !isPrivate, participantCount });

    const isBusinessChatroom = chatroomId === businessChatRoomId;

    let avatar;
    let name;

    if (isBusinessChatroom) {
      const isAvailable =
        this.props.availabilityStatus !== AVAILABILITY_STATUS.AWAY;
      name = isAvailable ? onlineTitle : offlineTitle;
      avatar = this.props.editorSettingsAvatar;
    } else {
      name = chatroom.name;
      avatar = chatroom.thumbnail
        ? { ...AVATARS.IMG.prop, imgUrl: chatroom.thumbnail }
        : AVATARS.NONE.prop;
    }

    const room = {
      id,
      name,
      userId: chatroom.otherParticipantId,
      avatar,
      createBy,
      isPrivate: chatroom.isPrivate,
    };

    this.props.setCurrentChatroom(room);
    this.setState({ widgetStates: WidgetStates.chatroom });
  };

  onProfileClick = () => {
    const { currentChatroom } = this.props;
    currentChatroom && wix.navigateToMemberProfile(currentChatroom.userId);
  };

  renderChatWidget = (props: any, childComp: any) => {
    const { locale, isResponsive } = this.props;

    const { isGroupChat } = this.state;
    return (
      <ChatWidgetWithUnreadCount
        isExpanded={this.isExpanded()}
        theme={this.props.theme}
        isMobile={this.isMobile()}
        isResponsive={isResponsive}
        locale={locale}
        available={this.shouldShowChat()}
        eventHooks={this.eventHooks}
        onProfileClick={isGroupChat ? undefined : this.onProfileClick}
        initiallyApplyExpandAnimation={false}
        {...props}
      >
        {this.isExpanded() && childComp}
      </ChatWidgetWithUnreadCount>
    );
  };

  getChatListState = () => {
    const { businessChatEnabled, t } = this.props;

    return this.renderChatWidget(
      {
        onlineTitle: t('social.chatroom-list.title'),
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <SocialChatList
        onSelectChatroom={this.handleChatroomSelection}
        onClickCreateNewChat={this.handleClickCreateNewChat}
        includeBusinessChatroom={businessChatEnabled}
      />,
    );
  };

  getChatCreationState = () => {
    const { t } = this.props;

    return this.renderChatWidget(
      {
        hideCollapseButton: true,
        onCancel: this.handleCreationCancel,
        onlineTitle: t('social.new-chat.title'),
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ChatCreation
        onCreate={this.handleOnCreateNewChat}
        onCreateGroupChat={() =>
          this.setState({
            widgetStates: WidgetStates.createGroupChat,
            numberOfSelected: 0,
          })
        }
      />,
    );
  };

  buildHeader = (title: string, subTitle: string) => {
    return (
      <div className={styles.titleHeader}>
        <div className={styles.titleFirstLine}>{title}</div>
        <div className={styles.titleSecondLine}>{subTitle}</div>
      </div>
    );
  };

  getChatCreationGroupState = () => {
    const { t } = this.props;

    const numberOfSelectedString = `${this.state.numberOfSelected}`;

    const chatroomHeaderTitle =
      this.state.numberOfSelected !== NO_HEADER
        ? this.buildHeader(
            t('social.group-chat.new-title'),
            t('social.group-chat.number-selected', {
              number: numberOfSelectedString,
            }),
          )
        : null;

    return this.renderChatWidget(
      {
        hideCollapseButton: true,
        onCancel: this.handleCreationCancel,
        onlineTitle: chatroomHeaderTitle,
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ChatCreation
        onCreate={this.handleOnCreateNewChat}
        onChangeHeader={(numberOfSelected) =>
          this.setState({ numberOfSelected })
        }
      />,
    );
  };

  handleUpdateGroupChatroom = async ({ name, image }) => {
    const { currentChatroom } = this.props;
    if (!currentChatroom) {
      return;
    }
    const { userId, createBy, id } = currentChatroom;

    const groupChatroomImage = { url: image };

    await socialServerApi.updateGroupChatRoom(
      currentChatroom.id,
      name,
      groupChatroomImage,
    );

    const avatar = image
      ? { ...AVATARS.IMG.prop, imgUrl: image }
      : AVATARS.NONE.prop;

    const room = {
      id,
      userId,
      name,
      avatar,
      createBy,
      isPrivate: false,
    };

    this.props.setCurrentChatroom(room);

    this.setState({ widgetStates: WidgetStates.chatroom });
  };

  getUpdateGroupChatState = () => {
    const { t, currentChatroom } = this.props;
    if (!currentChatroom) {
      throw new Error(
        'Current chatroom should be exists once Update Group Stage triggered',
      );
    }
    const numberOfMembersInGroupString = `${this.state.participantCount}`;
    const avatar: any = currentChatroom.avatar;

    return this.renderChatWidget(
      {
        hideCollapseButton: true,
        onCancel: () => this.setState({ widgetStates: WidgetStates.chatroom }),
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ChatroomUpdate
        image={avatar.imgUrl}
        name={currentChatroom.name}
        numberOfMembers={numberOfMembersInGroupString}
        submitButtonText={t('social.group-chat.done-button')}
        submitButtonCallback={this.handleUpdateGroupChatroom}
      />,
    );
  };

  getChatroomState = () => {
    const {
      locale,
      localizedTexts: { onlineMessage, offlineMessage },
      availabilityStatus,
      currentChatroom,
      t,
      visitorId,
      editorSettingsBehaviour: { responseTimeEnabled, responseTimeSelected },
      widgetSenderIdentity,
      widgetSenderData,
      experiments,
    } = this.props;

    const typicallyReplies = {
      isEnabled: responseTimeEnabled,
      period: responseTimeSelected,
    };

    const { isGroupChat } = this.state;
    const isAvailable = availabilityStatus !== AVAILABILITY_STATUS.AWAY;
    const messageForBusinessRoom = isAvailable ? onlineMessage : offlineMessage;
    if (!currentChatroom) {
      return null;
    }

    const messageDisplayOption: MessageDisplayConfig =
      isGroupChat || widgetSenderIdentity !== WidgetSenderIdentityType.ANONYMOUS
        ? {
            avatarOption: AvatarConfig.INCOMING,
            titleOption: TitleConfig.INCOMING,
          }
        : {
            avatarOption: AvatarConfig.NONE,
            titleOption: TitleConfig.NONE,
          };

    const chatroomHeaderTitle = isGroupChat
      ? this.buildHeader(
          currentChatroom.name,
          t('social.group-chat.number-members', {
            number: this.state.participantCount,
          }),
        )
      : currentChatroom.name;

    const menuItems =
      currentChatroom.createBy === visitorId
        ? this.menuItems.filter((menuItem) => menuItem.includeInGroupOwner)
        : this.menuItems;

    const menu = isGroupChat ? <HeaderMenu menuItems={menuItems} /> : null;

    const deliveryMethods = experiments.enabled(Experiments.ChatBadgeFromWidget)
      ? [DeliveryMethodId.Chat]
      : undefined;

    return this.renderChatWidget(
      {
        onClickBack: this.handleClickBack,
        avatar: currentChatroom.avatar,
        onlineTitle: chatroomHeaderTitle,
        onlineMessage: this.isBusinessChatroom()
          ? messageForBusinessRoom
          : undefined,
        menu,
        typicallyReplies: this.isBusinessChatroom()
          ? typicallyReplies
          : undefined,
        availabilityStatus,
      },
      <ChatRoomScreen
        chatroomId={currentChatroom.id}
        onUploadFile={this.handleUploadFile}
        appMode={AppMode.Widget}
        locale={locale}
        theme={this.props.theme}
        messageDisplayOption={messageDisplayOption}
        onRoomChanged={(room) => {
          const isEnrichedRecieved =
            room.participants.length === room.participantCount;
          if (isEnrichedRecieved) {
            this.setState({
              chatroom: room,
              participantCount: room.participantCount,
            });
          }
        }}
        sendersData={widgetSenderData}
        deliveryMethods={deliveryMethods}
      />,
    );
  };

  onMessageNotificationClick = async (message) => {
    const { widgetExpanded } = this.props;

    if (!widgetExpanded) {
      expandCollapseManager.expand({
        direction: 'uou',
        referrer: 'message indication',
      });
    }

    void this.handleChatroomSelection(message.chatroomId);
  };

  handleLeaveGroupChatroom = async () => {
    if (!this.props.currentChatroom) {
      return;
    }
    await socialServerApi.leaveGroupChatRoom(this.props.currentChatroom.id);
    this.props.setCurrentChatroom(null);
    this.setState({ widgetStates: WidgetStates.chatroomList });
  };

  getLeaveChatState = () => {
    const { t, currentChatroom } = this.props;
    if (!currentChatroom) {
      return;
    }

    const numberOfParticipant = `${this.state.participantCount}`;

    const chatroomHeaderTitle = this.buildHeader(
      currentChatroom.name,
      t('social.group-chat.number-members', {
        number: numberOfParticipant,
      }),
    );

    const textColor = _get(theme, 'room.message.textColor');

    return this.renderChatWidget(
      {
        onClickBack: () =>
          this.setState({ widgetStates: WidgetStates.chatroom }),
        hideCollapseButton: false,
        onlineTitle: chatroomHeaderTitle,
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ModalWidget
        fontColor={textColor}
        title={t('social.group-chat.leave-group.title')}
        description={t('social.group-chat.leave-group.description')}
        primaryButtonText={t('social.group-chat.leave-group.button.leave')}
        primaryButtonCallback={this.handleLeaveGroupChatroom}
        secondaryButtonText={t('social.group-chat.leave-group.button.cancel')}
        secondaryButtonCallback={() =>
          this.setState({
            widgetStates: WidgetStates.chatroom,
          })
        }
      />,
    );
  };

  getRemoveMemberFromRoomState = () => {
    const { t } = this.props;

    const onlineTitle = this.buildHeader(
      t('social.group-chat.member-list.title'),
      t('social.group-chat.member-list.subtitle', {
        membersCount: this.state.participantCount,
      }),
    );

    const textColor = _get(theme, 'room.message.textColor');

    const name = this.state.chatroom.participants
      .filter((p) => p.id === this.state.participantIdToRemove)
      .pop().displayData.name;

    const removeParticipant = async () => {
      const { chatroom, participantIdToRemove } = this.state;

      await socialServerApi.removeParticipantFromChatRoom(
        chatroom.id,
        participantIdToRemove,
      );

      await this.handleChatroomSelection(chatroom.id);
    };

    return this.renderChatWidget(
      {
        onClickBack: () =>
          this.setState({ widgetStates: WidgetStates.memberList }),
        hideCollapseButton: false,
        onlineTitle,
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ModalWidget
        fontColor={textColor}
        title={t('social.group-chat.remove-member-from-group.title', {
          name,
        })}
        description={t(
          'social.group-chat.remove-member-from-group.description',
        )}
        primaryButtonText={t(
          'social.group-chat.remove-member-from-group.primary-button.label',
        )}
        primaryButtonCallback={removeParticipant}
        secondaryButtonText={t(
          'social.group-chat.remove-member-from-group.secondary-button.label',
        )}
        secondaryButtonCallback={() => {
          this.setState({
            widgetStates: WidgetStates.memberList,
          });
        }}
      />,
    );
  };

  getMemberListState = () => {
    const { t } = this.props;

    const onlineTitle = this.buildHeader(
      t('social.group-chat.member-list.title'),
      t('social.group-chat.member-list.subtitle', {
        membersCount: this.state.participantCount,
      }),
    );

    const members: SiteMember[] = this.state.chatroom.participants.map(
      (participant) => ({
        userId: participant.id,
        name: participant.displayData.name,
        image: participant.displayData.image,
      }),
    );

    const onAddMembers = () =>
      this.setState({
        widgetStates: WidgetStates.addMembersToRoom,
        numberOfSelected: 0,
      });

    const onRemoveMember = (participantId: string) =>
      this.setState({
        widgetStates: WidgetStates.removeMemberFromRoom,
        participantIdToRemove: participantId,
      });

    const isAdmin =
      this.state.chatroom.createdBy === this.state.chatroom.participantId;

    return this.renderChatWidget(
      {
        onClickBack: () =>
          this.setState({ widgetStates: WidgetStates.chatroomList }),
        hideCollapseButton: false,
        onlineTitle,
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ChatroomMemberList
        members={members}
        onAddMembers={onAddMembers}
        onRemoveMember={isAdmin ? onRemoveMember : undefined}
        adminMemberIds={[this.state.chatroom.createdBy]}
      />,
    );
  };

  getAddMembersToRoomState = () => {
    const { t } = this.props;

    const onlineTitle = this.buildHeader(
      t('social.group-chat.add-members.title'),
      t('social.group-chat.number-selected', {
        number: String(this.state.numberOfSelected),
      }),
    );

    const onAdd = async (memberIds: string[]) => {
      const { chatroom } = this.state;
      await socialServerApi.addMembersToChatRoom(chatroom.id, memberIds);
      await this.handleChatroomSelection(chatroom.id);
    };

    return this.renderChatWidget(
      {
        onClickBack: () =>
          this.setState({ widgetStates: WidgetStates.memberList }),
        hideCollapseButton: false,
        onlineTitle,
        unfoldable: false,
        avatar: this.NONE_AVATAR,
      },
      <ChatroomAddMembers
        onChangeHeader={(numberOfSelected) =>
          this.setState({ numberOfSelected })
        }
        onAdd={onAdd}
        participants={this.state.chatroom.participants}
      />,
    );
  };

  render() {
    const { widgetStates } = this.state;
    const { isChatSdkInit, currentChatroom } = this.props;

    if (!isChatSdkInit) {
      return null;
    }

    let currentState;

    switch (widgetStates) {
      case WidgetStates.leaveGroupChat:
        currentState = this.getLeaveChatState();
        break;
      case WidgetStates.memberList:
        currentState = this.getMemberListState();
        break;
      case WidgetStates.addMembersToRoom:
        currentState = this.getAddMembersToRoomState();
        break;
      case WidgetStates.removeMemberFromRoom:
        currentState = this.getRemoveMemberFromRoomState();
        break;
      case WidgetStates.createPrivateChat:
        currentState = this.getChatCreationState();
        break;
      case WidgetStates.createGroupChat:
        currentState = this.getChatCreationGroupState();
        break;
      case WidgetStates.updateGroupChat:
        currentState = this.getUpdateGroupChatState();
        break;
      case WidgetStates.chatroomList:
        currentState = this.getChatListState();
        break;
      case WidgetStates.chatroom:
      default:
        currentState = this.getChatroomState();
    }

    const activeRoom = currentChatroom ? currentChatroom.id : undefined;

    return (
      <div onClick={this.handleOnClick}>
        {this.state.isStateNotificationEnabled && (
          <MessagePopup
            onClick={this.onMessageNotificationClick}
            activeRoom={activeRoom}
            isResponsive={this.props.isResponsive}
          />
        )}
        {currentState}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    theme: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    localizedTexts: getLocalizedTexts(state),
    editorSettingsLayout: state.editorSettingsLayout,
    visibilitySettings: state.visibilitySettings,
    locale: state.locale,
    viewMode: state.viewMode,
    shardId: state.shardId,
    businessChatRoomId: state.businessChatRoomId,
    isChatSdkInit: state.isChatSdkInit,
    deviceType: state.deviceType,
    businessChatEnabled: state.businessChatEnabled,
    availabilityStatus: state.availabilityStatus,
    widgetExpanded: state.widgetExpanded,
    currentChatroom: state.currentChatroom,
    isMessageNotificationEnabled: isMessageNotificationEnabled(state),
    visitorId: state.chatInitResult.visitorId,
    chatOnPage: state.chatOnPage,
    primaryColor: primaryColorOrDefault(state),
    widgetSenderIdentity: state.widgetSenderIdentity,
    widgetSenderData: state.widgetSenderData,
    host: state.props,
  };
}

const mapDispatchToProps = (dispatch) => ({
  initApp: (data) => dispatch(initApp(data)),
  firebaseGoOffline: () => dispatch(goOffline()),
  expandWidget: () => dispatch(expandWidget()),
  collapseWidget: () => dispatch(collapseWidget()),
  setCurrentChatroom: (data) => dispatch(setCurrentChatroom(data)),
  startPrivateChat: (data) => dispatch(startPrivateChat(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(withMobile(translate()(SocialChat))));
